import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FeedbackReceivedSection from "@/components/FeedbackReceived";
import {
  myCertificate,
  getCheckoutStatus, fetchMe, myProgress, myPeerReviewSubmissions,
  myPeerReviewModuleStatus, fetchAchievements,
} from "@/lib/apiClient";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowRight,
  BookOpen, Users, ShieldCheck, Circle, CheckCircle2, Trophy,
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { resolveModuleName } from "@/lib/i18nContent";

const TABS = ["self", "peer", "official"];

export default function Certificate() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [params, setParams] = useSearchParams();
  const [polling, setPolling] = useState(false);
  const { setUser } = useAuth();
  const tab = TABS.includes(params.get("tab")) ? params.get("tab") : "self";
  const setTab = (next) => setParams((p) => { const np = new URLSearchParams(p); np.set("tab", next); return np; }, { replace: true });

  const loadAll = () => { myCertificate().then(setData); };

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Return from Stripe — poll status until "paid" then refresh user level.
  // Kept even though the Official Certification tab's own upgrade CTA is
  // hidden for now (§ "Coming soon") — a learner who already started a
  // checkout before that change should still see it resolve correctly.
  const sessionIdParam = params.get("session_id");
  useEffect(() => {
    const cancelled = params.get("billing") === "cancelled";
    if (cancelled) {
      toast.info(t("cert.toast_cancelled"));
      setParams({}, { replace: true });
      return;
    }
    if (!sessionIdParam || polling) return;
    setPolling(true);
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const s = await getCheckoutStatus(sessionIdParam);
        if (s.payment_status === "paid") {
          toast.success(t("cert.toast_paid"));
          try { const me = await fetchMe(); setUser(me); } catch (_e) { /* non-fatal */ }
          loadAll();
          setPolling(false);
          setParams({}, { replace: true });
          return;
        }
        if (s.status === "expired") {
          toast.error(t("cert.toast_expired"));
          setPolling(false);
          setParams({}, { replace: true });
          return;
        }
        if (attempts >= 8) {
          toast.message(t("cert.toast_processing"));
          setPolling(false);
          return;
        }
        setTimeout(tick, 2000);
      } catch (e) {
        toast.error(e?.response?.data?.detail || t("cert.toast_verify_failed"));
        setPolling(false);
        setParams({}, { replace: true });
      }
    };
    tick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionIdParam]);

  if (!data) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        {polling && (
          <div className="max-w-[896px] mx-auto w-full px-5 sm:px-8 pt-6">
            <div className="rounded-xl bg-[#FFFBEB] border border-[#FED7AA] px-4 py-3 text-sm text-[#B45309]" data-testid="checkout-polling">
              {t("cert.verifying")}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="certificate-page">
      <Header />
      <main className="flex-1 max-w-[896px] mx-auto w-full px-5 sm:px-8 py-12">
        {polling && (
          <div className="mb-4 rounded-xl bg-[#FFFBEB] border border-[#FED7AA] px-4 py-3 text-sm text-[#B45309]" data-testid="checkout-polling">
            {t("cert.verifying")}
          </div>
        )}
        <div className="text-xs uppercase tracking-[0.22em] text-[#4B5563]">{t("cert.eyebrow")}</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-2 text-[#1F2937]">{t("cert.title")}</h1>

        {/* Self Assessment / Peer Assessment / Official Certification — all
            three certification layers live under this one hub now. */}
        <div className="mt-6 inline-flex flex-wrap items-center rounded-full border border-[#E7E0D2] bg-white overflow-hidden" data-testid="cert-hub-tabs">
          <button data-testid="cert-tab-self" onClick={() => setTab("self")}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition ${tab === "self" ? "bg-[#064E3B] text-white" : "text-[#4B5563] hover:text-[#1F2937]"}`}>
            <BookOpen className="w-4 h-4" /> {t("cert.tab_self")}
          </button>
          <button data-testid="cert-tab-peer" onClick={() => setTab("peer")}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition ${tab === "peer" ? "bg-[#064E3B] text-white" : "text-[#4B5563] hover:text-[#1F2937]"}`}>
            <Users className="w-4 h-4" /> {t("cert.tab_peer")}
          </button>
          <button data-testid="cert-tab-official" onClick={() => setTab("official")}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition ${tab === "official" ? "bg-[#064E3B] text-white" : "text-[#4B5563] hover:text-[#1F2937]"}`}>
            <ShieldCheck className="w-4 h-4" /> {t("cert.tab_official")}
          </button>
        </div>

        {tab === "self" && (
          <div className="mt-8">
            <SelfAssessmentPanel />
            <SingleScoreCard
              testid="self-score-card"
              label={t("cert.self_score_label")}
              value={data.score_breakdown?.module_marks || 0}
              hint={t("cert.self_score_hint")} />
          </div>
        )}

        {tab === "peer" && (
          <div className="mt-8" data-testid="cert-peer-tab">
            <PeerAssessmentPanel />
            <div className="max-w-3xl mt-8">
              <h2 className="font-heading text-xl font-semibold text-[#1F2937] mb-2">{t("feedback_received.title")}</h2>
              <PeerFeedbackReceived />
            </div>
          </div>
        )}

        {tab === "official" && (
          <div className="mt-8" data-testid="cert-official-tab">
            <div className="rounded-3xl bg-white border border-[#E7E0D2] p-10 sm:p-14 text-center" data-testid="cert-coming-soon">
              <ShieldCheck className="w-10 h-10 text-[#9CA3AF] mx-auto" />
              <div className="font-heading text-2xl sm:text-3xl text-[#1F2937] mt-4">{t("cert.official_coming_soon_title")}</div>
              <p className="text-sm text-[#4B5563] mt-3 max-w-md mx-auto">
                {t("cert.official_coming_soon_body")}
              </p>
            </div>
            {/* Always zero here — official certification isn't submitted or
                available yet, so no real score exists to show. */}
            <ScoreBreakdownCard sb={{ module_marks: 0, recitation_score: 0, final_score: 0 }} t={t} />
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

/**
 * Self Assessment tab — cross-module progress summary. Pulls the same
 * `myProgress()` payload the Dashboard uses, but focused purely on the
 * self-assessment (per-module pass/fail) layer.
 */
function SelfAssessmentPanel() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [achievements, setAchievements] = useState([]);
  useEffect(() => {
    myProgress().then(setData);
    fetchAchievements().then(setAchievements);
  }, []);

  if (!data) return <div className="h-24 bg-[#E7E0D2] rounded-2xl animate-pulse" />;

  const mods = data.progress?.modules || {};
  const modules = data.modules || [];
  const completedCount = modules.filter((m) => mods[m.code]?.passed).length;
  const allComplete = modules.length > 0 && completedCount === modules.length;
  const badge = achievements.find((a) => a.id === "all_modules");

  return (
    <div data-testid="self-assessment-panel">
      <div className="rounded-2xl bg-white border border-[#E7E0D2] p-6">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.18em] text-[#064E3B] font-semibold">{t("cert.modules_passed")}</div>
          <BookOpen className="w-5 h-5 text-[#064E3B]" />
        </div>
        <div className="mt-2 font-heading text-4xl font-semibold text-[#1F2937] tabular-nums" data-testid="self-assessment-count">
          {completedCount}/{modules.length}
        </div>
        <div className="mt-3 h-1.5 bg-[#E7E0D2] rounded-full overflow-hidden">
          <div className="h-full bg-[#064E3B] transition-all" style={{ width: `${Math.round(100 * completedCount / Math.max(modules.length, 1))}%` }} />
        </div>
      </div>

      {allComplete && badge && (
        <div className="mt-4 flex items-center gap-3 rounded-2xl bg-gradient-to-br from-[#FFFBEB] to-[#FEF3C7]/60 border border-[#FED7AA] p-4" data-testid="self-assessment-badge">
          <div className="w-10 h-10 rounded-xl bg-[#D97706] text-white grid place-items-center shrink-0">
            <Trophy className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading text-sm text-[#1F2937]">{badge.title}</div>
            <div className="text-xs text-[#4B5563]">{badge.desc}</div>
          </div>
        </div>
      )}

      <div className="mt-5 flex flex-col gap-2">
        {modules.map((m) => {
          const mp = mods[m.code] || {};
          return (
            <div key={m.code} data-testid={`self-assessment-${m.code}`} className="flex items-center gap-3 rounded-xl bg-white border border-[#E7E0D2] px-4 py-3">
              {mp.passed ? <CheckCircle2 className="w-4 h-4 text-[#10B981] shrink-0" /> : <Circle className="w-4 h-4 text-[#9CA3AF] shrink-0" />}
              <span className="flex-1 text-sm text-[#1F2937]">{m.n}. {resolveModuleName(t, m)}</span>
              {mp.passed
                ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#ECFDF5] text-[#064E3B] font-medium">{t("cert.status_passed")}</span>
                : (mp.attempts || 0) > 0
                  ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FEF2F2] text-[#EF4444] font-medium">{t("cert.status_in_progress")}</span>
                  : <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F5EFE3] text-[#4B5563] font-medium">{t("cert.status_not_started")}</span>}
              <Link to={`/modules/${m.code}`} className="text-[#064E3B] hover:underline text-xs font-medium inline-flex items-center gap-1">
                {t("cert.open")} <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Peer Assessment tab — mirrors SelfAssessmentPanel's shape exactly, but
 * for peer-review completeness: a module only counts "complete" here once
 * EVERY section/subsection has received 1 peer review (stricter than the
 * `peer_reviewed_all_modules` achievement, which only requires one section
 * per module) — powered by `GET /peer-reviews/my-module-status`.
 */
function PeerAssessmentPanel() {
  const { t } = useI18n();
  const [status, setStatus] = useState(null);
  const [achievements, setAchievements] = useState([]);
  useEffect(() => {
    myPeerReviewModuleStatus().then(setStatus);
    fetchAchievements().then(setAchievements);
  }, []);

  if (!status) return <div className="h-24 bg-[#E7E0D2] rounded-2xl animate-pulse" />;

  const modules = status.modules || [];
  const completedCount = modules.filter((m) => m.complete).length;
  const badge = achievements.find((a) => a.id === "peer_reviewed_all_modules");
  const totalUnits = modules.reduce((sum, m) => sum + m.total_units, 0);
  const reviewedUnits = modules.reduce((sum, m) => sum + m.reviewed_units, 0);
  const peerScore = Math.round(100 * reviewedUnits / Math.max(totalUnits, 1));

  return (
    <div data-testid="peer-assessment-panel">
      <div className="rounded-2xl bg-white border border-[#E7E0D2] p-6">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.18em] text-[#064E3B] font-semibold">{t("cert.modules_peer_reviewed")}</div>
          <Users className="w-5 h-5 text-[#064E3B]" />
        </div>
        <div className="mt-2 font-heading text-4xl font-semibold text-[#1F2937] tabular-nums" data-testid="peer-assessment-count">
          {completedCount}/{modules.length}
        </div>
        <div className="mt-3 h-1.5 bg-[#E7E0D2] rounded-full overflow-hidden">
          <div className="h-full bg-[#064E3B] transition-all" style={{ width: `${Math.round(100 * completedCount / Math.max(modules.length, 1))}%` }} />
        </div>
        <p className="mt-3 text-xs text-[#4B5563]">{t("cert.peer_review_hint")}</p>
      </div>

      {status.all_complete && badge && (
        <div className="mt-4 flex items-center gap-3 rounded-2xl bg-gradient-to-br from-[#FFFBEB] to-[#FEF3C7]/60 border border-[#FED7AA] p-4" data-testid="peer-assessment-badge">
          <div className="w-10 h-10 rounded-xl bg-[#D97706] text-white grid place-items-center shrink-0">
            <Trophy className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading text-sm text-[#1F2937]">{badge.title}</div>
            <div className="text-xs text-[#4B5563]">{badge.desc}</div>
          </div>
        </div>
      )}

      <div className="mt-5 flex flex-col gap-2">
        {modules.map((m) => (
          <div key={m.code} data-testid={`peer-assessment-${m.code}`} className="flex items-center gap-3 rounded-xl bg-white border border-[#E7E0D2] px-4 py-3">
            {m.complete ? <CheckCircle2 className="w-4 h-4 text-[#10B981] shrink-0" /> : <Circle className="w-4 h-4 text-[#9CA3AF] shrink-0" />}
            <span className="flex-1 text-sm text-[#1F2937]">{m.n}. {resolveModuleName(t, m)}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${m.complete ? "bg-[#ECFDF5] text-[#064E3B]" : "bg-[#F5EFE3] text-[#4B5563]"}`}>
              {t("cert.sections_reviewed", { reviewed: m.reviewed_units, count: m.total_units })}
            </span>
          </div>
        ))}
      </div>

      <SingleScoreCard
        testid="peer-score-card"
        label={t("cert.peer_score_label")}
        value={peerScore}
        hint={t("cert.peer_score_hint", { reviewed: reviewedUnits, total: totalUnits })} />
    </div>
  );
}

/**
 * Single, tab-specific score display — used on Self and Peer Assessment so
 * each shows its own real number instead of the blended 45/55 certificate
 * formula (which only makes sense once a certificate can actually be
 * issued — see the Official Certification tab).
 */
function SingleScoreCard({ label, value, hint, testid }) {
  return (
    <div className="mt-8 rounded-2xl bg-white border border-[#E7E0D2] p-6" data-testid={testid}>
      <div className="text-xs uppercase tracking-[0.18em] text-[#4B5563] font-semibold">{label}</div>
      <div className="mt-2 font-heading text-4xl font-semibold text-[#064E3B] tabular-nums">
        {value}<span className="text-xl text-[#4B5563]">%</span>
      </div>
      {hint && <div className="mt-2 text-xs text-[#4B5563]">{hint}</div>}
    </div>
  );
}

// Fetches the current user's peer-review submissions and shows the
// feedback they've received on them — moved here from the Dashboard.
function PeerFeedbackReceived() {
  const [submissions, setSubmissions] = useState([]);
  useEffect(() => { myPeerReviewSubmissions().then((r) => setSubmissions(r?.items || [])).catch(() => {}); }, []);
  return <FeedbackReceivedSection submissions={submissions} />;
}

/**
 * Final Score Formula — full breakdown of the 45/55 weighting, moved here
 * from the Dashboard so it lives alongside Official Certification.
 */
function ScoreBreakdownCard({ sb, t }) {
  if (!sb) return null;
  const mm = sb.module_marks || 0;
  const rs = sb.recitation_score || 0;
  const fs = sb.final_score || 0;
  return (
    <div className="mt-8 rounded-3xl bg-white border border-[#E7E0D2] p-6 sm:p-8" data-testid="score-breakdown-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-[#D97706] font-semibold">
            {t("dashboard.score_breakdown_eyebrow")}
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl text-[#1F2937] mt-2">{t("dashboard.score_breakdown_title")}</h2>
          <p className="text-sm text-[#4B5563] mt-2 max-w-md">{t("dashboard.score_breakdown_body")}</p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-[#4B5563]">{t("dashboard.score_final")}</div>
          <div className="font-heading text-5xl font-semibold text-[#064E3B] tabular-nums" data-testid="score-final">{fs}<span className="text-2xl text-[#4B5563]">%</span></div>
        </div>
      </div>

      {/* Two-column formula */}
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div className="rounded-2xl bg-[#FDFBF7] border border-[#E7E0D2] p-5" data-testid="score-modules">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.2em] text-[#4B5563] font-medium">{t("dashboard.score_modules")}</div>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#064E3B] text-white">× 45%</span>
          </div>
          <div className="mt-2 flex items-baseline gap-1">
            <div className="font-heading text-3xl font-semibold text-[#1F2937] tabular-nums">{mm}<span className="text-base text-[#4B5563]">%</span></div>
          </div>
          <div className="mt-3 h-2 bg-[#E7E0D2] rounded-full overflow-hidden">
            <div className="h-full bg-[#064E3B] transition-all" style={{ width: `${mm}%` }} />
          </div>
          <div className="mt-2 text-xs text-[#4B5563]">{t("dashboard.score_modules_hint")}</div>
        </div>
        <div className="rounded-2xl bg-[#FDFBF7] border border-[#E7E0D2] p-5" data-testid="score-recitation">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.2em] text-[#4B5563] font-medium">{t("dashboard.score_recitation")}</div>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#D97706] text-white">× 55%</span>
          </div>
          <div className="mt-2 flex items-baseline gap-1">
            <div className="font-heading text-3xl font-semibold text-[#1F2937] tabular-nums">{rs}<span className="text-base text-[#4B5563]">%</span></div>
          </div>
          <div className="mt-3 h-2 bg-[#E7E0D2] rounded-full overflow-hidden">
            <div className="h-full bg-[#D97706] transition-all" style={{ width: `${rs}%` }} />
          </div>
          <div className="mt-2 text-xs text-[#4B5563]">{t("dashboard.score_recitation_hint")}</div>
        </div>
      </div>
      <div className="mt-4 text-[11px] text-[#4B5563] italic">
        {t("dashboard.score_formula", { mm, rs, fs })}
      </div>
    </div>
  );
}
